import { useEffect, useRef, useState } from 'react'
import {
  Archive,
  Check,
  ChevronsUpDown,
  FlaskConical,
  House,
  LogOut,
  Plus,
  Trash2,
  User,
} from 'lucide-react'
import { useMyHouseholds } from '@/hooks/useMyHouseholds'
import { useCurrentHousehold } from '@/hooks/useCurrentHousehold'
import { useSampleHousehold } from '@/hooks/useSampleHousehold'
import { Avatar } from '@/components/Avatar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { MENU_SURFACE } from '@/components/ui/menu-surface'
import { CreateHouseholdModal } from '@/components/CreateHouseholdModal'

const ROLE_LABELS: Record<string, string> = { OWNER: 'Owner', MEMBER: 'Member', VIEWER: 'Viewer' }

/** One combined control for "who am I" and "which household am I acting on" — instead of a
 *  separate profile button and household switcher sitting in the same tile, this is a single
 *  trigger (avatar + name + active household + role as a subtitle) that opens one menu covering
 *  both: switch/create households, then Profile/Sign Out below. Archived households are included
 *  in the list (not hidden), tagged, since viewing one read-only is just a switch away. */
export function AccountMenu({
  displayName,
  avatarPreset,
  collapsed = false,
  onProfileClick,
  onSignOut,
}: {
  displayName: string
  avatarPreset: string
  /** When true (sidebar collapsed to icon-only width), the trigger shows just the avatar. */
  collapsed?: boolean
  onProfileClick: () => void
  onSignOut: () => void
}) {
  const { data: households } = useMyHouseholds()
  const { householdId, role, switchHousehold } = useCurrentHousehold()
  const { sample, startCreate, startDelete, dialogs } = useSampleHousehold()
  const [menuOpen, setMenuOpen] = useState(false)
  const [showCreate, setShowCreate] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false)
      }
    }
    if (menuOpen) document.addEventListener('mousedown', handleOutside)
    return () => document.removeEventListener('mousedown', handleOutside)
  }, [menuOpen])

  const all = households ?? []
  const current = all.find((h) => h.id === householdId) ?? all[0]
  const currentIsArchived = !!current?.archivedAt

  return (
    <div className="relative" ref={menuRef}>
      <Button
        variant="ghost"
        onClick={() => setMenuOpen((o) => !o)}
        aria-expanded={menuOpen}
        aria-haspopup="menu"
        className="relative h-auto w-full items-center justify-start gap-3 rounded-lg px-3 py-3 text-sm"
      >
        <Avatar avatarPreset={avatarPreset} size="md" />
        {!collapsed && (
          <>
            <div className="flex-1 min-w-0 space-y-0.5 text-left">
              <span className="block truncate text-sm font-semibold text-[var(--fl-heading)]">
                {displayName}
              </span>
              {current && (
                <span className="flex items-center gap-1 text-xs text-[var(--fl-text-2)]">
                  {currentIsArchived ? (
                    <Archive size={12} className="shrink-0" />
                  ) : (
                    <House size={12} className="shrink-0" />
                  )}
                  <span className="truncate">{current.name}</span>
                </span>
              )}
              {role && <Badge tone="primary">{ROLE_LABELS[role] ?? role}</Badge>}
            </div>
            <ChevronsUpDown size={14} className="shrink-0 text-[var(--fl-text-2)]" />
          </>
        )}
      </Button>

      {menuOpen && (
        <div role="menu" className={`${MENU_SURFACE} absolute bottom-14 left-2 z-50 w-56 py-1.5`}>
          {all.length > 1 && (
            <>
              <p className="px-4 pb-1 pt-1 text-[10px] font-semibold uppercase tracking-wider text-[var(--fl-text-2)]">
                Households
              </p>
              {all.map((h) => (
                <button
                  key={h.id}
                  type="button"
                  role="menuitemradio"
                  aria-checked={h.id === current?.id}
                  onClick={() => {
                    switchHousehold(h.id)
                    setMenuOpen(false)
                  }}
                  className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-[var(--fl-text-1)] hover:bg-[var(--fl-surface-hover)]"
                >
                  <Check
                    size={14}
                    className={
                      h.id === current?.id
                        ? 'shrink-0 text-[var(--fl-primary)]'
                        : 'shrink-0 opacity-0'
                    }
                  />
                  <span className="min-w-0 flex-1 truncate">{h.name}</span>
                  {h.isSample && (
                    <Badge tone="neutral" className="shrink-0">
                      Sample
                    </Badge>
                  )}
                  {h.archivedAt && (
                    <Badge tone="warning" className="shrink-0">
                      Archived
                    </Badge>
                  )}
                </button>
              ))}
            </>
          )}
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setMenuOpen(false)
              setShowCreate(true)
            }}
            className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-[var(--fl-primary)] hover:bg-[var(--fl-surface-hover)]"
          >
            <Plus size={14} className="shrink-0" />
            Create household
          </button>
          {sample ? (
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setMenuOpen(false)
                startDelete()
              }}
              className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-[var(--fl-error)] hover:bg-[var(--fl-surface-hover)]"
            >
              <Trash2 size={14} className="shrink-0" />
              Delete sample household
            </button>
          ) : (
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setMenuOpen(false)
                startCreate()
              }}
              className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-[var(--fl-text-1)] hover:bg-[var(--fl-surface-hover)]"
            >
              <FlaskConical size={14} className="shrink-0" />
              Explore with sample data
            </button>
          )}

          <div className="my-1.5 border-t border-[var(--fl-border)]" />

          <Button
            variant="ghost"
            role="menuitem"
            onClick={() => {
              setMenuOpen(false)
              onProfileClick()
            }}
            className="h-auto w-full justify-start gap-2.5 rounded-none px-4 py-2 text-sm text-[var(--fl-text-1)]"
          >
            <User size={14} /> Profile
          </Button>
          <Button
            variant="ghost"
            role="menuitem"
            onClick={() => {
              setMenuOpen(false)
              onSignOut()
            }}
            className="h-auto w-full justify-start gap-2.5 rounded-none px-4 py-2 text-sm text-[var(--fl-text-1)]"
          >
            <LogOut size={14} /> Sign Out
          </Button>
        </div>
      )}

      {dialogs}
      {showCreate && <CreateHouseholdModal onClose={() => setShowCreate(false)} />}
    </div>
  )
}
